import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  FileText,
  Download,
  Calendar,
  Filter,
  Search,
  Eye,
  Edit,
  Trash2,
  BarChart3,
  TrendingUp,
  Users,
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";

// Mock data - substituir com dados reais da API
const mockReports = [
  {
    id: "1",
    name: "Relatório Mensal de Orçamentos",
    description: "Análise completa dos orçamentos do mês atual",
    type: "monthly",
    status: "completed",
    createdAt: "2024-01-20",
    createdBy: "João Silva",
    fileSize: "2.4 MB",
    format: "PDF"
  },
  {
    id: "2",
    name: "Análise de Conversão Q4 2023",
    description: "Taxa de conversão e métricas do 4º trimestre",
    type: "quarterly",
    status: "completed",
    createdAt: "2024-01-15",
    createdBy: "Maria Santos",
    fileSize: "1.8 MB",
    format: "Excel"
  },
  {
    id: "3",
    name: "Performance por Vendedor",
    description: "Ranking e desempenho individual da equipe",
    type: "performance",
    status: "processing",
    createdAt: "2024-01-22",
    createdBy: "Pedro Costa",
    fileSize: "-",
    format: "PDF"
  },
  {
    id: "4",
    name: "Relatório de Serviços Mais Vendidos",
    description: "Análise dos serviços com maior demanda",
    type: "services",
    status: "scheduled",
    createdAt: "2024-01-23",
    createdBy: "Ana Oliveira",
    fileSize: "-",
    format: "PDF"
  }
];

const reportTypes = [
  { value: "monthly", label: "Relatório Mensal", icon: Calendar },
  { value: "quarterly", label: "Relatório Trimestral", icon: BarChart3 },
  { value: "annual", label: "Relatório Anual", icon: TrendingUp },
  { value: "performance", label: "Performance", icon: Users },
  { value: "services", label: "Serviços", icon: FileText },
  { value: "conversion", label: "Conversão", icon: DollarSign },
  { value: "custom", label: "Personalizado", icon: Filter }
];

const statusConfig = {
  completed: {
    label: "Concluído",
    variant: "default" as const,
    icon: CheckCircle,
    color: "text-green-600"
  },
  processing: {
    label: "Processando",
    variant: "secondary" as const,
    icon: Clock,
    color: "text-blue-600"
  },
  scheduled: {
    label: "Agendado",
    variant: "secondary" as const,
    icon: Calendar,
    color: "text-yellow-600"
  },
  failed: {
    label: "Falhou",
    variant: "destructive" as const,
    icon: XCircle,
    color: "text-red-600"
  }
};

export default function BudgetReportsPage() {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedType, setSelectedType] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [showNewReportDialog, setShowNewReportDialog] = useState(false);

  const filteredReports = mockReports.filter(report => {
    const matchesSearch = report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         report.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = selectedType === "all" || report.type === selectedType;
    const matchesStatus = selectedStatus === "all" || report.status === selectedStatus;

    return matchesSearch && matchesType && matchesStatus;
  });

  const handleCreateReport = (reportType: string) => {
    // Implementar lógica de criação de relatório
    console.log("Creating report:", reportType);
    setShowNewReportDialog(false);
  };

  const handleDownloadReport = (reportId: string) => {
    // Implementar lógica de download
    console.log("Downloading report:", reportId);
  };

  const handleDeleteReport = (reportId: string) => {
    // Implementar lógica de exclusão
    console.log("Deleting report:", reportId);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Relatórios de Orçamentos</h1>
          <p className="text-muted-foreground">
            Gere e gerencie relatórios detalhados sobre orçamentos
          </p>
        </div>
        <Dialog open={showNewReportDialog} onOpenChange={setShowNewReportDialog}>
          <DialogTrigger asChild>
            <Button>
              <FileText className="h-4 w-4 mr-2" />
              Novo Relatório
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Criar Novo Relatório</DialogTitle>
              <DialogDescription>
                Selecione o tipo de relatório que deseja gerar
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                {reportTypes.map((type) => {
                  const Icon = type.icon;
                  return (
                    <Button
                      key={type.value}
                      variant="outline"
                      className="h-20 flex-col gap-2"
                      onClick={() => handleCreateReport(type.value)}
                    >
                      <Icon className="h-6 w-6" />
                      <span className="text-sm">{type.label}</span>
                    </Button>
                  );
                })}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total de Relatórios</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{mockReports.length}</div>
            <p className="text-xs text-muted-foreground">
              2 criados este mês
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Concluídos</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockReports.filter(r => r.status === "completed").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Prontos para download
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processando</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockReports.filter(r => r.status === "processing").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Em andamento
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Agendados</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {mockReports.filter(r => r.status === "scheduled").length}
            </div>
            <p className="text-xs text-muted-foreground">
              Na fila
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar relatórios..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="w-48">
            <SelectValue placeholder="Tipo de relatório" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            {reportTypes.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={selectedStatus} onValueChange={setSelectedStatus}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="completed">Concluídos</SelectItem>
            <SelectItem value="processing">Processando</SelectItem>
            <SelectItem value="scheduled">Agendados</SelectItem>
            <SelectItem value="failed">Falharam</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Reports Table */}
      <Card>
        <CardHeader>
          <CardTitle>Relatórios Gerados</CardTitle>
          <CardDescription>
            Histórico de relatórios criados e seu status atual
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome do Relatório</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Criado por</TableHead>
                <TableHead>Tamanho</TableHead>
                <TableHead className="text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredReports.map((report) => {
                const currentStatusConfig = statusConfig[report.status];
                const StatusIcon = currentStatusConfig.icon;
                const reportType = reportTypes.find(t => t.value === report.type);
                const TypeIcon = reportType?.icon || FileText;

                return (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div>
                        <div className="font-medium">{report.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {report.description}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <TypeIcon className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm">
                          {reportType?.label || report.type}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={currentStatusConfig.variant} className="flex items-center gap-1 w-fit">
                        <StatusIcon className="h-3 w-3" />
                        {currentStatusConfig.label}
                      </Badge>
                    </TableCell>
                    <TableCell>{report.createdAt}</TableCell>
                    <TableCell>{report.createdBy}</TableCell>
                    <TableCell>
                      {report.fileSize === "-" ? (
                        <span className="text-muted-foreground">-</span>
                      ) : (
                        <span className="text-sm">{report.fileSize}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {report.status === "completed" && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownloadReport(report.id)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                        )}
                        <Button variant="ghost" size="sm">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {filteredReports.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Nenhum relatório encontrado com os filtros selecionados
            </div>
          )}
        </CardContent>
      </Card>

      {/* Scheduled Reports */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Relatórios Agendados
          </CardTitle>
          <CardDescription>
            Relatórios configurados para geração automática
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground border-2 border-dashed rounded-lg">
            <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <h3 className="font-semibold mb-2">Nenhum relatório agendado</h3>
            <p className="text-sm mb-4">
              Configure relatórios automáticos para receber atualizações periódicas
            </p>
            <Button variant="outline">
              Configurar Agendamento
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}